import React, { useState, useMemo } from 'react';
import { CustomSelectDropdown, MultiSelectDropdown } from './HelperComponents';

const ActiveFiltersSummary = ({ filters, onClearFilter }) => {
    const activeFilters = [];

    if (filters.brand) {
        activeFilters.push({ key: 'brand', label: `Brand: ${filters.brand}`, value: 'brand' });
    }

    if (filters.category) {
        activeFilters.push({ key: 'category', label: `Category: ${filters.category}`, value: 'category' });
    }

    if (filters.manufacturer) {
        activeFilters.push({ key: 'manufacturer', label: `Manufacturer: ${filters.manufacturer}`, value: 'manufacturer' });
    }

    if (filters.ingredients && filters.ingredients.length > 0) {
        activeFilters.push({ key: 'ingredients', label: `Ingredients: ${filters.ingredients.length} selected`, value: 'ingredients' });
    }

    if (filters.specialFilters && filters.specialFilters.length > 0) {
        activeFilters.push({ key: 'specialFilters', label: `Special Filters: ${filters.specialFilters.length} selected`, value: 'specialFilters' });
    }

    if (filters.nutritionalFilters && Object.keys(filters.nutritionalFilters).length > 0) {
        const nutritionalCount = Object.keys(filters.nutritionalFilters).length;
        activeFilters.push({ key: 'nutritionalFilters', label: `Nutritional Filters: ${nutritionalCount} active`, value: 'nutritionalFilters' });
    }

    if (activeFilters.length === 0) {
        return null;
    }

    return (
        <div className="active-filters-summary">
            <div className="active-filters-header">
                <span className="active-filters-title">Active Filters:</span>
            </div>
            <div className="active-filters-tags">
                {activeFilters.map(filter => (
                    <span key={filter.key} className="active-filter-tag">
                        {filter.label}
                        <button
                            className="remove-filter-btn"
                            onClick={() => onClearFilter(filter.value)}
                            title="Remove this filter"
                        >
                            ×
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
};

const FilterPanel = ({ products, filters, setFilters, isCompact = false }) => {
    const [showNutritionalFilters, setShowNutritionalFilters] = useState(false);
    const [nutritionalFilters, setNutritionalFilters] = useState([]);

    // Helper function to get filtered products based on current filters (excluding the current filter type)
    const getFilteredProducts = (excludeFilter = null) => {
        return products.filter(product => {
            // Brand filter
            if (excludeFilter !== 'brand' && filters.brand && product.extracted_brand_name !== filters.brand) {
                return false;
            }

            // Category filter
            if (excludeFilter !== 'category' && filters.category && product.category_json?.["sub-category 3"] !== filters.category) {
                return false;
            }

            // Manufacturer filter
            if (excludeFilter !== 'manufacturer' && filters.manufacturer && product.extracted_manufacturer_name !== filters.manufacturer) {
                return false;
            }

            // Ingredients filter
            if (excludeFilter !== 'ingredients' && filters.ingredients && filters.ingredients.length > 0) {
                const productIngredients = getProductIngredients(product);
                const hasAnyIngredient = filters.ingredients.some(ingredient =>
                    productIngredients.some(prodIng =>
                        prodIng.toLowerCase().includes(ingredient.toLowerCase())
                    )
                );
                if (!hasAnyIngredient) return false;
            }

            return true;
        });
    };

    // Helper function to extract ingredients from a product
    const getProductIngredients = (product) => {
        let ingredientArray = [];
        if (Array.isArray(product.ingredient_list)) {
            ingredientArray = product.ingredient_list;
        } else if (typeof product.ingredient_list === 'string' && product.ingredient_list.trim() !== '') {
            try {
                const parsed = JSON.parse(product.ingredient_list);
                if (Array.isArray(parsed)) {
                    ingredientArray = parsed;
                } else {
                    ingredientArray = product.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
                }
            } catch {
                ingredientArray = product.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
            }
        }

        return ingredientArray.map(ingredient => {
            let cleanIngredient = ingredient.trim()
                .replace(/\[[^\]]*\]/g, '')
                .replace(/\([^)]*\)/g, '')
                .replace(/\d+\.?\d*%?/g, '')
                .replace(/\)/g, '')
                .replace(/\]\)/g, '')
                .replace(/& INS 0/g, '')
                .trim();

            const parts = cleanIngredient.split(',');
            return parts.map(part => {
                let finalPart = part.trim();
                if (finalPart && finalPart.length > 2) {
                    let normalizedPart = finalPart.toLowerCase();
                    if (normalizedPart.endsWith('s') && normalizedPart.length > 3) {
                        normalizedPart = normalizedPart.slice(0, -1);
                    }
                    return normalizedPart;
                }
                return '';
            }).filter(Boolean);
        }).flat();
    };

    // Generate dynamic filter options based on other active filters
    const brands = useMemo(() => {
        const filteredProducts = getFilteredProducts('brand');
        return [...new Set(filteredProducts.map(p => p.extracted_brand_name).filter(Boolean))].sort();
    }, [products, filters]);

    const categories = useMemo(() => {
        const filteredProducts = getFilteredProducts('category');
        return [...new Set(filteredProducts.map(p => p.category_json?.["sub-category 3"]).filter(Boolean))].sort();
    }, [products, filters]);

    const manufacturers = useMemo(() => {
        const filteredProducts = getFilteredProducts('manufacturer');
        return [...new Set(filteredProducts.map(p => p.extracted_manufacturer_name).filter(Boolean))].sort();
    }, [products, filters]);

    // Extract and clean ingredients from backend data
    const ingredients = useMemo(() => {
        const filteredProducts = getFilteredProducts('ingredients');
        const allIngredients = new Set();

        filteredProducts.forEach(product => {
            // Process both ins_e_ingredients_list and ingredient_list
            const ingredientSources = [];

            // Process ins_e_ingredients_list (structured data)
            if (product.ins_e_ingredients_list && product.ins_e_ingredients_list.trim()) {
                try {
                    // Parse the semicolon-separated JSON objects
                    const ingredientObjects = product.ins_e_ingredients_list.split('};').map(item => {
                        if (!item.endsWith('}')) item += '}';
                        return item.trim();
                    }).filter(item => item && item !== 'NaN');

                    ingredientObjects.forEach(ingredientStr => {
                        try {
                            const ingredientData = JSON.parse(ingredientStr);
                            if (ingredientData.ingredient_name) {
                                ingredientSources.push(ingredientData.ingredient_name);
                            }
                        } catch (e) {
                            // Skip invalid entries
                        }
                    });
                } catch (e) {
                    // Skip if parsing fails
                }
            }

            // Process ingredient_list (free text)
            if (product.ingredient_list && product.ingredient_list.trim()) {
                let ingredientArray = [];
                if (Array.isArray(product.ingredient_list)) {
                    ingredientArray = product.ingredient_list;
                } else if (typeof product.ingredient_list === 'string') {
                    try {
                        const parsed = JSON.parse(product.ingredient_list);
                        if (Array.isArray(parsed)) {
                            ingredientArray = parsed;
                        } else {
                            ingredientArray = product.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
                        }
                    } catch {
                        ingredientArray = product.ingredient_list.split(';').map(s => s.trim()).filter(Boolean);
                    }
                }

                ingredientArray.forEach(ingredient => {
                    ingredientSources.push(ingredient);
                });
            }

            // Process all collected ingredients
            ingredientSources.forEach(ingredient => {
                // Clean ingredient: remove brackets [], parentheses (), percentages, and specific unwanted patterns
                let cleanIngredient = ingredient.trim()
                    .replace(/\[[^\]]*\]/g, '') // Remove square brackets and content
                    .replace(/\([^)]*\)/g, '') // Remove parentheses and content
                    .replace(/\d+\.?\d*%?/g, '') // Remove percentages and numbers
                    .replace(/\)/g, '') // Remove standalone closing parentheses
                    .replace(/\]\)/g, '') // Remove "])" pattern
                    .replace(/& INS 0/g, '') // Remove "& INS 0" pattern
                    .trim();

                // Split on commas to handle multi-ingredient entries
                const parts = cleanIngredient.split(',');
                parts.forEach(part => {
                    let finalPart = part.trim();
                    if (finalPart && finalPart.length > 2) {
                        // Advanced normalization to group similar ingredients
                        let normalizedPart = finalPart.toLowerCase();

                        // Extract main ingredient from various specification patterns
                        // 1. Parenthetical expressions: "acidity regulator (citric acid)" → "acidity regulator"
                        const parenMatch = normalizedPart.match(/^([^()]+)\s*\([^)]*\)\s*$/);
                        if (parenMatch) {
                            normalizedPart = parenMatch[1].trim();
                        }

                        // 2. Colon specifications: "acidity regulator: citric acid" → "acidity regulator"
                        const colonMatch = normalizedPart.match(/^([^:]+):\s*.+$/);
                        if (colonMatch) {
                            normalizedPart = colonMatch[1].trim();
                        }

                        // 3. Common ingredient variations - extract base ingredient
                        // Handle extract/powder/concentrate variations
                        normalizedPart = normalizedPart
                            .replace(/\s+(extract|ext\.?|powder|concentrate|juice|oil|leaf|root|fruit|seed|berry|dried|spray|dried|pure|natural|organic|wild|fresh)\s*$/gi, '') // Remove common suffixes
                            .replace(/\s+(extract|ext\.?|powder|concentrate|juice|oil|leaf|root|fruit|seed|berry|dried|spray|dried|pure|natural|organic|wild|fresh)\s+/gi, ' ') // Remove common modifiers in middle
                            .replace(/\s*\*\s*/g, '') // Remove asterisks
                            .replace(/\s*#\s*/g, '') // Remove hash symbols
                            .replace(/\s*&\s*/g, ' ') // Replace & with space
                            .replace(/\s+/g, ' ') // Normalize multiple spaces
                            .replace(/^\s*-\s*/, '') // Remove leading dashes
                            .replace(/\s*-\s*$/, '') // Remove trailing dashes
                            .trim();

                        // 4. Handle specific ingredient groupings
                        const ingredientMappings = {
                            'ashwagandha': ['ashwagandha', 'ashwagandha root', 'ashwagandha ext', 'ashwagandha extract', 'ashwagandha mg'],
                            'vitamin c': ['vitamin c', 'ascorbic acid', 'vitamin c ascorbic acid'],
                            'vitamin d': ['vitamin d', 'cholecalciferol', 'ergocalciferol'],
                            'vitamin b12': ['vitamin b12', 'cyanocobalamin', 'methylcobalamin'],
                            'calcium': ['calcium', 'calcium carbonate', 'calcium citrate'],
                            'magnesium': ['magnesium', 'magnesium oxide', 'magnesium citrate'],
                            'zinc': ['zinc', 'zinc oxide', 'zinc citrate'],
                            'iron': ['iron', 'ferrous', 'ferric'],
                            'omega 3': ['omega 3', 'fish oil', 'epa', 'dha', 'ala'],
                            'probiotic': ['probiotic', 'bacillus', 'lactobacillus', 'bifidobacterium'],
                            'fiber': ['fiber', 'fibre', 'inulin', 'fructooligosaccharide'],
                            'antioxidant': ['antioxidant', 'vitamin e', 'tocopherol'],
                            'sweetener': ['sweetener', 'stevia', 'sucralose', 'aspartame'],
                            'color': ['color', 'colour', 'artificial color', 'natural color'],
                            'flavor': ['flavor', 'flavour', 'artificial flavor', 'natural flavor'],
                            'acidity regulator': ['acidity regulator', 'acid', 'citric', 'malic', 'tartaric', 'phosphoric']
                            // 'fos': ['fos', 'fos dietary', 'fructo oligosaccharide', 'fructo oligosachhride', 'fructo-oligo saccharide', 'fructo-oligosaccharide', 'fructo-oligosaccharides qs.', 'fructooligosacchoride', 'fructooligosachharide']
                        };

                        // Check if this ingredient matches any known grouping
                        let groupedIngredient = normalizedPart;
                        for (const [baseIngredient, variations] of Object.entries(ingredientMappings)) {
                            if (variations.some(variation =>
                                normalizedPart.includes(variation) ||
                                variation.includes(normalizedPart) ||
                                normalizedPart === variation
                            )) {
                                groupedIngredient = baseIngredient;
                                break;
                            }
                        }

                        // Remove trailing 's' to treat singular/plural as same (only if length > 3)
                        if (groupedIngredient.endsWith('s') && groupedIngredient.length > 3) {
                            groupedIngredient = groupedIngredient.slice(0, -1);
                        }

                        // Skip very short or generic terms
                        if (groupedIngredient.length > 2 && !['and', 'the', 'for', 'with', 'from', 'into', 'in', 'on', 'at', 'by', 'of', 'or'].includes(groupedIngredient)) {
                            allIngredients.add(groupedIngredient);
                        }
                    }
                });
            });
        });

        // Convert Set to Array and sort
        const finalIngredients = [...allIngredients].sort();
        console.log('All unique ingredients from backend:', finalIngredients);
        console.log('Total unique ingredients:', finalIngredients.length);
        return finalIngredients;
    }, [products, filters]);

    const handleFilterChange = (filterName, value) => setFilters(prev => ({ ...prev, [filterName]: value }));

    const addNutritionalFilter = () => {
        const newFilter = {
            id: Date.now(),
            field: 'energy_kcal',
            operator: 'gt',
            value: ''
        };
        setNutritionalFilters(prev => [...prev, newFilter]);
    };

    const updateNutritionalFilter = (id, field, value) => {
        setNutritionalFilters(prev =>
            prev.map(filter =>
                filter.id === id ? { ...filter, [field]: value } : filter
            )
        );

        // Update the main filters
        const updatedFilters = nutritionalFilters.map(filter =>
            filter.id === id ? { ...filter, [field]: value } : filter
        );

        const nutritionalFiltersObj = {};
        updatedFilters.forEach(filter => {
            if (filter.value !== '' && filter.value !== null) {
                nutritionalFiltersObj[filter.field] = {
                    operator: filter.operator,
                    value: parseFloat(filter.value)
                };
            }
        });

        setFilters(prev => ({ ...prev, nutritionalFilters: nutritionalFiltersObj }));
    };

    const removeNutritionalFilter = (id) => {
        const updatedFilters = nutritionalFilters.filter(filter => filter.id !== id);
        setNutritionalFilters(updatedFilters);

        const nutritionalFiltersObj = {};
        updatedFilters.forEach(filter => {
            if (filter.value !== '' && filter.value !== null) {
                nutritionalFiltersObj[filter.field] = {
                    operator: filter.operator,
                    value: parseFloat(filter.value)
                };
            }
        });

        setFilters(prev => ({ ...prev, nutritionalFilters: nutritionalFiltersObj }));
    };

    const nutritionalOptions = [
        { value: 'energy_kcal', label: 'Energy (kcal)' },
        { value: 'saturated_fat_g', label: 'Saturated Fat (g)' },
        { value: 'total_sugars_g', label: 'Total Sugars (g)' },
        { value: 'sodium_mg', label: 'Sodium (mg)' },
        { value: 'fruit_veg_nlm_percent', label: 'Fruit/Veg (%)' },
        { value: 'fibre_g', label: 'Fibre (g)' },
        { value: 'protein_g', label: 'Protein (g)' },
        { value: 'vitamin_d_iu', label: 'Vitamin D (IU)' },
        { value: 'vitamin_b12_mcg', label: 'Vitamin B12 (mcg)' },
        { value: 'vitamin_b9_mcg', label: 'Vitamin B9 (mcg)' },
        { value: 'vitamin_b6_mg', label: 'Vitamin B6 (mg)' },
        { value: 'vitamin_b5_mg', label: 'Vitamin B5 (mg)' },
        { value: 'calcium_mg', label: 'Calcium (mg)' },
        { value: 'iron_mg', label: 'Iron (mg)' },
        { value: 'magnesium_mg', label: 'Magnesium (mg)' },
        { value: 'zinc_mg', label: 'Zinc (mg)' },
        { value: 'selenium_mcg', label: 'Selenium (mcg)' },
        { value: 'epa_mg', label: 'EPA (mg)' },
        { value: 'dha_mg', label: 'DHA (mg)' },
        { value: 'ala_mg', label: 'ALA (mg)' },
        { value: 'inositol_mg', label: 'Inositol (mg)' },
        { value: 'probiotics_cfu', label: 'Probiotics (CFU)' },
        { value: 'tributyrin_mg', label: 'Tributyrin (mg)' }
    ];

    const specialFilterOptions = [
        { value: 'filter_vitamin_d_60k_iu', label: 'Vitamin D 60,000 IU' },
        { value: 'filter_vitamin_d_5k_iu', label: 'Vitamin D 5,000 IU' },
        { value: 'filter_vitamin_d_2k_iu', label: 'Vitamin D 2,000 IU' },
        { value: 'filter_vitamin_b12_1500_mcg', label: 'Vitamin B12 1,500 mcg' },
        { value: 'filter_vitamin_b12_500_mcg', label: 'Vitamin B12 500 mcg' },
        { value: 'filter_magnesium_150_mg', label: 'Magnesium 150 mg' },
        { value: 'filter_vitd_calcium_magnesium', label: 'Vitamin D + Calcium + Magnesium' },
        { value: 'filter_b12_ala_zinc_selenium_inositol', label: 'B12 + ALA + Zinc + Selenium + Inositol' },
        { value: 'filter_b_complex_plus', label: 'B-Complex Plus' },
        { value: 'filter_iron_folic_acid', label: 'Iron + Folic Acid' },
        { value: 'filter_high_dose_epa_dha', label: 'High Dose EPA/DHA' },
        { value: 'filter_vegan_high_dose_epa_dha', label: 'Vegan High Dose EPA/DHA' },
        { value: 'filter_dha_dominant', label: 'DHA Dominant' },
        { value: 'filter_unflavoured_isolate', label: 'Unflavoured Isolate' },
        { value: 'filter_unflavoured_concentrate', label: 'Unflavoured Concentrate' },
        { value: 'filter_probiotics_20b_cfu', label: 'Probiotics 20B CFU' },
        { value: 'filter_pre_pro_post_biotics', label: 'Pre/Pro/Post Biotics' }
    ];

    if (isCompact) {
        // For compact mode, use the same dynamic filtering but limit to top options
        const selectedBrand = filters.brand;
        const selectedCategory = filters.category;
        const selectedIngredients = filters.ingredients || [];

        // Create brand options - include selected brand + top 5 from filtered results
        const brandOptions = [{ value: '', label: `All Brands (${brands.length})` }];
        if (selectedBrand && !brandOptions.some(opt => opt.value === selectedBrand)) {
            brandOptions.push({ value: selectedBrand, label: selectedBrand });
        }
        brands.slice(0, 5).forEach(brand => {
            if (!brandOptions.some(opt => opt.value === brand)) {
                brandOptions.push({ value: brand, label: brand });
            }
        });

        // Create category options - include selected category + top 5 from filtered results
        const categoryOptions = [{ value: '', label: `All Categories (${categories.length})` }];
        if (selectedCategory && !categoryOptions.some(opt => opt.value === selectedCategory)) {
            categoryOptions.push({ value: selectedCategory, label: selectedCategory });
        }
        categories.slice(0, 5).forEach(cat => {
            if (!categoryOptions.some(opt => opt.value === cat)) {
                categoryOptions.push({ value: cat, label: cat });
            }
        });

        // Create ingredient options - include selected ingredients + top 10 from filtered results
        const ingredientOptions = [];
        selectedIngredients.forEach(ingredient => {
            if (!ingredientOptions.some(opt => opt.value === ingredient)) {
                ingredientOptions.push({ value: ingredient, label: ingredient });
            }
        });
        ingredients.slice(0, 10).forEach(ingredient => {
            if (!ingredientOptions.some(opt => opt.value === ingredient)) {
                ingredientOptions.push({ value: ingredient, label: ingredient });
            }
        });

        return (
            <div className="filter-panel compact">
                <div className="filter-header compact">
                    <h3>Quick Filters</h3>
                </div>

                <div className="filters-grid compact">
                    <div className="filter-group compact">
                        <CustomSelectDropdown
                            options={brandOptions}
                            value={filters.brand}
                            onChange={(value) => handleFilterChange('brand', value)}
                            placeholder="Brand"
                        />
                    </div>

                    <div className="filter-group compact">
                        <CustomSelectDropdown
                            options={categoryOptions}
                            value={filters.category}
                            onChange={(value) => handleFilterChange('category', value)}
                            placeholder="Category"
                        />
                    </div>

                    <div className="filter-group compact">
                        <MultiSelectDropdown
                            options={ingredientOptions}
                            selectedValues={filters.ingredients || []}
                            onChange={(values) => handleFilterChange('ingredients', values)}
                            placeholder="Ingredients"
                        />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="filter-panel">
            <div className="filter-header">
                <h2>Smart Filters</h2>
                <p className="filter-description">Discover products that match your criteria</p>
            </div>

            <div className="filters-grid">
                <div className="filter-group">
                    <label>🏢 Brand</label>
                    <CustomSelectDropdown
                        options={[
                            { value: '', label: `All Brands (${brands.length})` },
                            ...brands.map(brand => ({ value: brand, label: brand }))
                        ]}
                        value={filters.brand}
                        onChange={(value) => handleFilterChange('brand', value)}
                        placeholder={`All Brands (${brands.length})`}
                    />
                </div>

                <div className="filter-group">
                    <label>📂 Category</label>
                    <CustomSelectDropdown
                        options={[
                            { value: '', label: `All Categories (${categories.length})` },
                            ...categories.map(cat => ({ value: cat, label: cat }))
                        ]}
                        value={filters.category}
                        onChange={(value) => handleFilterChange('category', value)}
                        placeholder={`All Categories (${categories.length})`}
                    />
                </div>

                <div className="filter-group">
                    <label>🏭 Manufacturer</label>
                    <CustomSelectDropdown
                        options={[
                            { value: '', label: `All Manufacturers (${manufacturers.length})` },
                            ...manufacturers.map(manufacturer => ({ value: manufacturer, label: manufacturer }))
                        ]}
                        value={filters.manufacturer || ''}
                        onChange={(value) => handleFilterChange('manufacturer', value)}
                        placeholder={`All Manufacturers (${manufacturers.length})`}
                    />
                </div>

                <div className="filter-group">
                    <label>🥗 Ingredient</label>
                    <MultiSelectDropdown
                        options={ingredients.map(ingredient => ({ value: ingredient, label: ingredient }))}
                        selectedValues={filters.ingredients || []}
                        onChange={(values) => handleFilterChange('ingredients', values)}
                        placeholder={`Select Ingredients (${ingredients.length})`}
                    />
                </div>

                {/* <div className="filter-group">
                    <label>🎯 Special Filters</label>
                    <MultiSelectDropdown
                        options={specialFilterOptions}
                        selectedValues={filters.specialFilters || []}
                        onChange={(values) => handleFilterChange('specialFilters', values)}
                        placeholder="Select Special Filters"
                    />
                </div> */}
            </div>

            {/* <div className="nutritional-filters-section">
                {!showNutritionalFilters ? (
                    <button
                        className="add-nutritional-btn"
                        onClick={() => {
                            setShowNutritionalFilters(true);
                            // Automatically add the first filter when opening
                            if (nutritionalFilters.length === 0) {
                                addNutritionalFilter();
                            }
                        }}
                    >
                        + Add Nutritional Filters
                    </button>
                ) : (
                    <div className="nutritional-filters">
                        <div className="nutritional-header">
                            <h3>Nutritional Filters</h3>
                            <button
                                className="hide-nutritional-btn"
                                onClick={() => {
                                    setShowNutritionalFilters(false);
                                    setNutritionalFilters([]);
                                    setFilters(prev => ({ ...prev, nutritionalFilters: {} }));
                                }}
                            >
                                Hide
                            </button>
                        </div>

                        {nutritionalFilters.map(filter => (
                            <div key={filter.id} className="nutritional-filter-row">
                                <select
                                    value={filter.field}
                                    onChange={e => updateNutritionalFilter(filter.id, 'field', e.target.value)}
                                >
                                    {nutritionalOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>

                                <select
                                    value={filter.operator}
                                    onChange={e => updateNutritionalFilter(filter.id, 'operator', e.target.value)}
                                >
                                    <option value="gt">Greater than (&gt;)</option>
                                    <option value="lt">Less than (&lt;)</option>
                                    <option value="eq">Equal to (=)</option>
                                </select>

                                <input
                                    type="number"
                                    placeholder="Value"
                                    value={filter.value}
                                    onChange={e => updateNutritionalFilter(filter.id, 'value', e.target.value)}
                                />

                                <button
                                    className="remove-filter-btn"
                                    onClick={() => removeNutritionalFilter(filter.id)}
                                >
                                    ×
                                </button>
                            </div>
                        ))}

                        <button
                            className="add-filter-btn"
                            onClick={addNutritionalFilter}
                        >
                            + Add Another Filter
                        </button>
                    </div>
                )}
            </div> */}
        </div>
    );
};

export { ActiveFiltersSummary, FilterPanel };
